/**
 * M79 — Warehouse SKUs line-item-property injector
 *
 * Reads `variant.metafields.custom.warehouse_skus` (JSON `[{sku, qty}]`) from
 * a Liquid-populated registry on `window.__warehouseSkus` and attaches a hidden
 * `_warehouse_skus` line-item property to every cart-add request, so:
 *
 *   1. The Shopify order's line item carries the property
 *   2. ShipStation's native Shopify connector turns it into an Item Option
 *   3. `parseOrderItems.js` (app_suite_apis) reads it for the kit-component
 *      fulfillment expansion
 *   4. The packing slip renders `WAREHOUSESKUS:` inline with each line
 *
 * Coverage: PDP form submit, quick-add buttons, buy-together, recommended
 * accessories, predictive-search ATC — anything that POSTs to /cart/add or
 * fetches /cart/add.js. Does NOT cover abandoned-cart-recovery re-add (P2).
 *
 * The registry is populated by `snippets/warehouse-skus-registry.liquid`,
 * which renders `window.__warehouseSkus[variantId] = [{sku,qty},...]` for
 * every variant rendered on the page.
 *
 * Fallback when registry doesn't have the variant: no property is injected
 * (keeps cart line behavior identical to pre-M79). Downstream `parseOrderItems`
 * has a WASP-based fallback for KIT items, so fulfillment still works for KITs
 * even without the property. The packing slip just won't carry the WAREHOUSESKUS
 * line for that order.
 */
(function () {
  'use strict';

  // Global registry: variantId -> [{sku, qty}, ...]
  window.__warehouseSkus = window.__warehouseSkus || {};

  function getComponents(variantId) {
    if (!variantId) return null;
    var key = String(variantId);
    var entry = window.__warehouseSkus[key];
    if (Array.isArray(entry) && entry.length > 0) return entry;
    return null;
  }

  // KIT-AUDIT-2026-05-21: `_warehouse_skus` is contractually a PER-UNIT
  // descriptor of the line — `parseOrderItems.js` re-applies the order line
  // quantity downstream when it expands components for the warehouse scan-out.
  // The prior `perUnit * qty` baked the cart-add quantity into the property,
  // so the scan-out double-counted (order qty 2 -> scan-out said 4) for any
  // non-KIT line ordered qty >= 2. Emit the per-unit component qty ONLY.
  // (The cart-add quantity is still carried natively on the cart line.)
  // Callers may still pass a line-qty arg; it is intentionally ignored here.
  function buildPropValue(components) {
    if (!Array.isArray(components) || components.length === 0) return null;
    var parts = [];
    for (var i = 0; i < components.length; i++) {
      var c = components[i];
      if (!c || !c.sku) continue;
      var perUnit = parseInt(c.qty, 10);
      if (!isFinite(perUnit) || perUnit < 1) perUnit = 1;
      parts.push(c.sku + '=' + perUnit);
    }
    return parts.length > 0 ? parts.join(', ') : null;
  }

  // Inject _warehouse_skus into a single cart-add item object (JSON body shape)
  //
  // KIT-AUDIT-2026-05-20 (round 2): the prior `if (props._warehouse_skus == null)`
  // guard early-skipped whenever a value was already present. Shopify theme code
  // reuses item objects and a variant registry across variant switches, so a
  // stale sibling-variant value would survive (or never get corrected). This
  // was reproduced on a multi-variant KIT product after the form-submit fix
  // (f6440f3) shipped. Same correctness rule as the
  // form-submit path: always RECOMPUTE and OVERWRITE with the CURRENT variant's
  // value on every cart-add — never trust a pre-existing `_warehouse_skus`.
  function injectIntoItem(item) {
    if (!item || !item.id) return item;
    var components = getComponents(item.id);
    if (!components) return item;
    var value = buildPropValue(components, item.quantity || 1);
    if (!value) return item;
    var props = item.properties && typeof item.properties === 'object'
      ? Object.assign({}, item.properties)
      : {};
    props._warehouse_skus = value;
    return Object.assign({}, item, { properties: props });
  }

  function tryInjectJsonBody(bodyStr) {
    try {
      var body = JSON.parse(bodyStr);
      var changed = false;
      if (body.items && Array.isArray(body.items)) {
        body.items = body.items.map(function (it) {
          var injected = injectIntoItem(it);
          if (injected !== it) changed = true;
          return injected;
        });
      } else if (body.id != null) {
        var injected = injectIntoItem(body);
        if (injected !== body) {
          body = injected;
          changed = true;
        }
      }
      return changed ? JSON.stringify(body) : bodyStr;
    } catch (e) {
      return bodyStr;
    }
  }

  // KIT-AUDIT-2026-05-20 (round 2): dropped the `formData.has(...) -> return`
  // early-out. Shopify theme JS reuses the same FormData instance across variant
  // switches, so a stale sibling value would survive. `.set()` removes ALL
  // existing `properties[_warehouse_skus]` entries and writes exactly one fresh
  // value for the CURRENT variant + quantity — recompute-and-overwrite every add.
  function tryInjectFormData(formData) {
    var variantId = formData.get('id');
    if (!variantId) return formData;
    var qty = formData.get('quantity') || 1;
    var components = getComponents(variantId);
    if (!components) return formData;
    var value = buildPropValue(components, qty);
    if (!value) return formData;
    formData.set('properties[_warehouse_skus]', value);
    return formData;
  }

  // KIT-AUDIT-2026-05-20 (round 2): dropped the `params.has(...) -> return`
  // early-out for the same reason. `URLSearchParams.set()` removes all existing
  // entries for the key and appends one — recompute-and-overwrite every add so a
  // reused/cloned body string can never carry a stale sibling-variant value.
  function tryInjectUrlEncoded(bodyStr) {
    if (typeof URLSearchParams === 'undefined') return bodyStr;
    try {
      var params = new URLSearchParams(bodyStr);
      var variantId = params.get('id');
      if (!variantId) return bodyStr;
      var qty = params.get('quantity') || 1;
      var components = getComponents(variantId);
      if (!components) return bodyStr;
      var value = buildPropValue(components, qty);
      if (!value) return bodyStr;
      params.set('properties[_warehouse_skus]', value);
      return params.toString();
    } catch (e) {
      return bodyStr;
    }
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    var s = String(url);
    return s.indexOf('/cart/add') !== -1;
  }

  // -- fetch interceptor --
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (isCartAddUrl(url) && init && init.body) {
          if (typeof init.body === 'string') {
            // either JSON or url-encoded
            var trimmed = init.body.trim();
            if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
              init.body = tryInjectJsonBody(init.body);
            } else if (trimmed.indexOf('id=') !== -1 || trimmed.indexOf('quantity=') !== -1) {
              init.body = tryInjectUrlEncoded(init.body);
            }
          } else if (init.body instanceof FormData) {
            init.body = tryInjectFormData(init.body);
          } else if (window.URLSearchParams && init.body instanceof window.URLSearchParams) {
            // re-wrap as URLSearchParams to add a param
            var s = init.body.toString();
            init.body = new URLSearchParams(tryInjectUrlEncoded(s));
          }
        }
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('[warehouse-skus-injector] fetch shim error:', e);
        }
      }
      return origFetch.apply(this, arguments);
    };
  }

  // -- XMLHttpRequest interceptor (older theme code paths) --
  if (window.XMLHttpRequest) {
    var origOpen = window.XMLHttpRequest.prototype.open;
    var origSend = window.XMLHttpRequest.prototype.send;
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__M79_isCartAdd = isCartAddUrl(url);
      return origOpen.apply(this, arguments);
    };
    window.XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__M79_isCartAdd && body) {
          if (typeof body === 'string') {
            var trimmed = body.trim();
            if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
              body = tryInjectJsonBody(body);
            } else if (trimmed.indexOf('id=') !== -1 || trimmed.indexOf('quantity=') !== -1) {
              body = tryInjectUrlEncoded(body);
            }
            arguments[0] = body;
          } else if (body instanceof FormData) {
            arguments[0] = tryInjectFormData(body);
          }
        }
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('[warehouse-skus-injector] xhr shim error:', e);
        }
      }
      return origSend.apply(this, arguments);
    };
  }

  // -- form submit interceptor (page-reload ATC fallback) --
  // Listens during capture so we add the hidden input before native form submission.
  //
  // KIT-AUDIT-2026-05-20: the same <form action="/cart/add"> DOM node is reused
  // across multiple add-to-cart actions (Shopify section-rerender variant
  // switching keeps the form node, only swaps the `id` input). The previous
  // logic appended a `properties[_warehouse_skus]` input and then early-returned
  // on every later submit because that stale input still existed -> the second
  // variant added carried the FIRST variant's warehouse_skus value (wrong-sibling
  // bug). Fix: on every
  // submit, REMOVE any input THIS injector previously created (tagged with
  // `data-warehouse-skus-injected`) and re-create it fresh for the current
  // variant. We only ever touch inputs we tagged — a theme/customer-supplied
  // `properties[_warehouse_skus]` input is left untouched.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action) return;
    if (!isCartAddUrl(form.action)) return;
    try {
      // Drop any input WE injected on a prior submit of this reused form.
      var stale = form.querySelectorAll('input[data-warehouse-skus-injected="1"]');
      for (var s = 0; s < stale.length; s++) {
        if (stale[s].parentNode) stale[s].parentNode.removeChild(stale[s]);
      }
      var variantInput = form.querySelector('input[name="id"], select[name="id"]');
      if (!variantInput) return;
      var variantId = variantInput.value;
      // If a theme/customer-supplied (un-tagged) _warehouse_skus input exists,
      // respect it and do nothing — only our own tagged inputs are managed here.
      if (form.querySelector('input[name="properties[_warehouse_skus]"]')) return;
      var qtyInput = form.querySelector('input[name="quantity"]');
      var qty = qtyInput ? qtyInput.value : 1;
      var components = getComponents(variantId);
      if (!components) return;
      var value = buildPropValue(components, qty);
      if (!value) return;
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[_warehouse_skus]';
      hidden.value = value;
      hidden.setAttribute('data-warehouse-skus-injected', '1');
      form.appendChild(hidden);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[warehouse-skus-injector] form-submit shim error:', err);
      }
    }
  }, true);
}());
